import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ImportIcon } from '../components/Icons';
import { fetchPlatforms, parseCSV, autoDetectColumns, importHoldings, addHolding, detectCurrency, authHeaders } from '../utils/api';

const PLATFORMS = [
  'Wealthsimple', 'Questrade', 'TD Direct Investing', 'RBC Direct Investing',
  'BMO InvestorLine', 'Scotia iTRADE', 'CIBC Investor\'s Edge', 'National Bank Direct Brokerage',
  'Desjardins Online Brokerage', 'HSBC InvestDirect',
  'Robinhood', 'Interactive Brokers', 'Fidelity', 'Charles Schwab',
  'E*TRADE', 'Vanguard', 'Merrill Edge', 'Webull', 'Moomoo', 'Ally Invest',
  'Other'
];

const ACCOUNT_TYPES = [
  'TFSA', 'RRSP', 'FHSA', 'RESP', 'LIRA', 'RRIF',
  'Non-Registered', 'Cash', 'Margin',
  'Roth IRA', 'Traditional IRA', '401(k)',
  'Other'
];

const CURRENCIES = ['USD', 'CAD'];

const TABS = [
  { key: 'manual', label: 'Manual Entry', icon: 'M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z' },
  { key: 'screenshot-trade', label: 'Trade Screenshot', icon: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' },
  { key: 'screenshot-holdings', label: 'Holdings Screenshot', icon: 'M16 16l-4-4-4 4M12 12v9M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3' },
  { key: 'csv', label: 'CSV Import', icon: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3' },
];

function ImportPortfolio() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('manual');

  return (
    <div>
      <div className="page-title">
        <ImportIcon size={28} color="#00ff9d" />
        <h1>Import Holdings</h1>
      </div>
      <p className="hero-subtitle">Add holdings manually, from screenshots, or CSV files</p>

      <div style={{ display: 'flex', gap: 6, marginBottom: 24, flexWrap: 'wrap', background: 'rgba(255,255,255,0.03)', borderRadius: 14, padding: 4, border: '1px solid rgba(255,255,255,0.06)' }}>
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              flex: 1,
              minWidth: 120,
              padding: '10px 12px',
              borderRadius: 10,
              border: 'none',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 500,
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              background: activeTab === tab.key ? 'rgba(0,255,157,0.12)' : 'transparent',
              color: activeTab === tab.key ? '#00ff9d' : '#64748b',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d={tab.icon} />
            </svg>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'manual' && <ManualEntry navigate={navigate} />}
      {activeTab === 'screenshot-trade' && <ScreenshotTrade navigate={navigate} />}
      {activeTab === 'screenshot-holdings' && <ScreenshotHoldings navigate={navigate} />}
      {activeTab === 'csv' && <CSVImport navigate={navigate} />}
    </div>
  );
}

function ManualEntry({ navigate }) {
  const [ticker, setTicker] = useState('');
  const [shares, setShares] = useState('');
  const [buyPrice, setBuyPrice] = useState('');
  const [platform, setPlatform] = useState('');
  const [customPlatform, setCustomPlatform] = useState('');
  const [accountType, setAccountType] = useState('');
  const [customAccountType, setCustomAccountType] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [msg, setMsg] = useState(null);

  const brokerLabel = getBrokerLabel(platform, customPlatform, accountType, customAccountType);
  const platformValid = platform && (platform !== 'Other' || customPlatform.trim());
  const accountValid = accountType && (accountType !== 'Other' || customAccountType.trim());

  async function handleAdd() {
    if (!platformValid) { setMsg({ type: 'error', text: 'Please select a trading platform.' }); return; }
    if (!accountValid) { setMsg({ type: 'error', text: 'Please select an account type.' }); return; }
    if (!ticker.trim()) { setMsg({ type: 'error', text: 'Please enter a ticker symbol.' }); return; }
    if (!shares || parseFloat(shares) <= 0) { setMsg({ type: 'error', text: 'Please enter a valid number of shares.' }); return; }
    if (!buyPrice || parseFloat(buyPrice) <= 0) { setMsg({ type: 'error', text: 'Please enter a valid buy price.' }); return; }
    const res = await addHolding(ticker.trim().toUpperCase(), parseFloat(shares), parseFloat(buyPrice), brokerLabel, currency);
    if (res.error) {
      setMsg({ type: 'warning', text: res.error });
    } else {
      setMsg({ type: 'success', text: `${ticker.trim().toUpperCase()} added to ${brokerLabel} (${currency})!` });
      setTicker('');
      setShares('');
      setBuyPrice('');
    }
    setTimeout(() => setMsg(null), 3000);
  }

  return (
    <div>
      {msg && <div className={`alert-box alert-${msg.type}`}>{msg.text}</div>}

      <div className="glass-card">
        <h3 style={{ color: '#e2e8f0', marginTop: 0, marginBottom: 16 }}>Add a holding manually</h3>
        <PlatformSelect platform={platform} setPlatform={setPlatform} customPlatform={customPlatform} setCustomPlatform={setCustomPlatform} accountType={accountType} setAccountType={setAccountType} customAccountType={customAccountType} setCustomAccountType={setCustomAccountType} currency={currency} setCurrency={setCurrency} />
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Ticker Symbol</label>
            <input className="input-field" value={ticker} onChange={e => setTicker(e.target.value)} placeholder="AAPL or SHOP.TO" />
          </div>
          <div className="form-group">
            <label className="form-label">Average Buy Price ({currency})</label>
            <input className="input-field" type="number" min="0.0001" step="0.01" value={buyPrice} onChange={e => setBuyPrice(e.target.value)} placeholder="150.00" />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Number of Shares</label>
            <input className="input-field" type="number" min="0.0001" step="1" value={shares} onChange={e => setShares(e.target.value)} placeholder="10" />
          </div>
          <div className="form-group" />
        </div>
      </div>

      <button className="btn-primary btn-full" onClick={handleAdd} disabled={!platformValid || !accountValid}>Add Holding</button>
    </div>
  );
}

function ScreenshotTrade({ navigate }) {
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [platform, setPlatform] = useState('');
  const [customPlatform, setCustomPlatform] = useState('');
  const [accountType, setAccountType] = useState('');
  const [customAccountType, setCustomAccountType] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [confirming, setConfirming] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleFile = useCallback((file) => {
    if (!file || !file.type.startsWith('image/')) return;
    setImageFile(file);
    setResult(null);
    setError(null);
    setSuccess(false);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);
  }, []);

  useEffect(() => {
    const handlePaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          handleFile(items[i].getAsFile());
          break;
        }
      }
    };
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [handleFile]);

  const handlePasteClick = async () => {
    try {
      const clipboardItems = await navigator.clipboard.read();
      for (const item of clipboardItems) {
        const imageType = item.types.find(t => t.startsWith('image/'));
        if (imageType) {
          const blob = await item.getType(imageType);
          handleFile(new File([blob], 'pasted-screenshot.png', { type: imageType }));
          return;
        }
      }
      setError('No image found in clipboard.');
      setTimeout(() => setError(null), 4000);
    } catch {
      setError('Could not access clipboard. Try Ctrl+V instead.');
      setTimeout(() => setError(null), 4000);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  }, [handleFile]);

  const handleAnalyze = async () => {
    if (!imageFile) return;
    setAnalyzing(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('image', imageFile);
      const res = await fetch('/api/trade-import', { method: 'POST', body: formData, headers: authHeaders() });
      const data = await res.json();
      if (data.error) setError(data.error);
      else {
        setResult(data);
        if (data.platform_guess) {
          const match = PLATFORMS.find(p => p.toLowerCase() === data.platform_guess.toLowerCase());
          if (match) setPlatform(match);
          else { setPlatform('Other'); setCustomPlatform(data.platform_guess); }
        }
      }
    } catch { setError('Failed to analyze image.'); }
    setAnalyzing(false);
  };

  const brokerLabel = getBrokerLabel(platform, customPlatform, accountType, customAccountType);
  const platformValid = platform && (platform !== 'Other' || customPlatform.trim());
  const accountValid = accountType && (accountType !== 'Other' || customAccountType.trim());

  const handleConfirm = async () => {
    if (!result || !platformValid || !accountValid) return;
    setConfirming(true);
    try {
      const res = await fetch('/api/confirm-trade', { method: 'POST', headers: authHeaders({ 'Content-Type': 'application/json' }), body: JSON.stringify({ ...result, broker: brokerLabel, currency }) });
      const data = await res.json();
      if (data.success) setSuccess(true);
      else setError(data.error || 'Failed to confirm trade');
    } catch { setError('Failed to confirm trade'); }
    setConfirming(false);
  };

  const reset = () => { setImageFile(null); setImagePreview(null); setResult(null); setError(null); setPlatform(''); setCustomPlatform(''); setAccountType(''); setCustomAccountType(''); setCurrency('USD'); setSuccess(false); };

  if (success) {
    return (
      <div className="glass-card" style={{ textAlign: 'center', padding: '50px 30px' }}>
        <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#00ff9d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 12 }}>
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
        </svg>
        <h2 style={{ color: '#00ff9d', marginBottom: 8 }}>Trade Recorded</h2>
        <p style={{ color: '#94a3b8', marginBottom: 8 }}>{result?.action} {result?.shares} shares of <strong style={{ color: '#e2e8f0' }}>{result?.ticker}</strong> at {currency} {result?.price_per_share?.toFixed(2)}</p>
        <p style={{ color: '#64748b', marginBottom: 24 }}>via {brokerLabel}</p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button className="btn-primary" onClick={reset}>Import Another</button>
          <button className="btn-secondary" onClick={() => navigate('/portfolio')}>View Portfolio</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {error && <div className="alert-box alert-error">{error}</div>}
      {!imagePreview && <UploadZone dragOver={dragOver} setDragOver={setDragOver} handleDrop={handleDrop} handlePasteClick={handlePasteClick} fileRef={fileRef} cameraRef={cameraRef} handleFile={handleFile} label="trade confirmation" />}

      {imagePreview && !result && (
        <div className="glass-card" style={{ textAlign: 'center', padding: '30px' }}>
          <img src={imagePreview} alt="Trade screenshot" style={{ maxWidth: '100%', maxHeight: 400, borderRadius: 12, marginBottom: 20, border: '1px solid rgba(0,255,157,0.2)' }} />
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="btn-primary" onClick={handleAnalyze} disabled={analyzing} style={analyzing ? { opacity: 0.7 } : { boxShadow: '0 0 20px rgba(0,255,157,0.3)' }}>
              {analyzing ? <><span className="spinner" style={{ marginRight: 8 }} />Analyzing...</> : 'Analyze with AI Vision'}
            </button>
            <button className="btn-secondary" onClick={reset}>Choose Different Image</button>
          </div>
        </div>
      )}

      {result && (
        <div className="glass-card" style={{ padding: '30px' }}>
          <h3 style={{ color: '#00ff9d', marginBottom: 6 }}>Trade Details Extracted</h3>
          <p style={{ color: '#64748b', fontSize: 12, marginBottom: 20 }}>Click any field to edit if the AI returned incorrect values</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
            <EditableField label="Ticker" value={result.ticker || ''} onChange={v => setResult(prev => ({ ...prev, ticker: v.toUpperCase() }))} />
            <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: 10, padding: '12px 16px' }}>
              <div style={{ color: '#64748b', fontSize: 11, marginBottom: 3, textTransform: 'uppercase', letterSpacing: 1 }}>Action</div>
              <select value={result.action || 'BUY'} onChange={e => setResult(prev => ({ ...prev, action: e.target.value }))} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, color: result.action === 'BUY' ? '#00ff9d' : '#ff4d4d', fontSize: 17, fontWeight: 600, padding: '4px 8px', width: '100%', fontFamily: 'Inter, sans-serif', cursor: 'pointer' }}>
                <option value="BUY" style={{ background: '#0f172a' }}>BUY</option>
                <option value="SELL" style={{ background: '#0f172a' }}>SELL</option>
              </select>
            </div>
            <EditableField label="Shares" value={result.shares ?? ''} onChange={v => setResult(prev => ({ ...prev, shares: parseFloat(v) || 0 }))} type="number" />
            <EditableField label="Price/Share" value={result.price_per_share ?? ''} onChange={v => setResult(prev => ({ ...prev, price_per_share: parseFloat(v) || 0 }))} type="number" prefix="$" />
            <EditableField label="Total" value={result.total_amount ?? ''} onChange={v => setResult(prev => ({ ...prev, total_amount: parseFloat(v) || 0 }))} type="number" prefix="$" />
            <EditableField label="Date" value={result.trade_date || ''} onChange={v => setResult(prev => ({ ...prev, trade_date: v }))} />
          </div>
          <PlatformSelect platform={platform} setPlatform={setPlatform} customPlatform={customPlatform} setCustomPlatform={setCustomPlatform} accountType={accountType} setAccountType={setAccountType} customAccountType={customAccountType} setCustomAccountType={setCustomAccountType} currency={currency} setCurrency={setCurrency} />
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button className="btn-primary btn-full" onClick={handleConfirm} disabled={confirming || !platformValid || !accountValid} style={!confirming && platformValid && accountValid ? { boxShadow: '0 0 20px rgba(0,255,157,0.3)' } : { opacity: 0.5 }}>
              {confirming ? 'Confirming...' : 'Confirm & Update Portfolio'}
            </button>
            <button className="btn-secondary" onClick={reset} style={{ flex: 'none' }}>Start Over</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ScreenshotHoldings({ navigate }) {
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [platform, setPlatform] = useState('');
  const [customPlatform, setCustomPlatform] = useState('');
  const [accountType, setAccountType] = useState('');
  const [customAccountType, setCustomAccountType] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [confirming, setConfirming] = useState(false);
  const [success, setSuccess] = useState(false);
  const [addedCount, setAddedCount] = useState(0);

  const handleFile = useCallback((file) => {
    if (!file || !file.type.startsWith('image/')) return;
    setImageFile(file);
    setResult(null);
    setError(null);
    setSuccess(false);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);
  }, []);

  useEffect(() => {
    const handlePaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          handleFile(items[i].getAsFile());
          break;
        }
      }
    };
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [handleFile]);

  const handlePasteClick = async () => {
    try {
      const clipboardItems = await navigator.clipboard.read();
      for (const item of clipboardItems) {
        const imageType = item.types.find(t => t.startsWith('image/'));
        if (imageType) {
          const blob = await item.getType(imageType);
          handleFile(new File([blob], 'pasted-screenshot.png', { type: imageType }));
          return;
        }
      }
      setError('No image found in clipboard.');
      setTimeout(() => setError(null), 4000);
    } catch {
      setError('Could not access clipboard. Try Ctrl+V instead.');
      setTimeout(() => setError(null), 4000);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  }, [handleFile]);

  const handleAnalyze = async () => {
    if (!imageFile) return;
    setAnalyzing(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('image', imageFile);
      const res = await fetch('/api/sync-holdings', { method: 'POST', body: formData, headers: authHeaders() });
      const data = await res.json();
      if (data.error) setError(data.error);
      else {
        setResult(data);
        if (data.platform_guess) {
          const match = PLATFORMS.find(p => p.toLowerCase() === data.platform_guess.toLowerCase());
          if (match) setPlatform(match);
          else { setPlatform('Other'); setCustomPlatform(data.platform_guess); }
        }
      }
    } catch { setError('Failed to analyze image.'); }
    setAnalyzing(false);
  };

  const removeHolding = (idx) => setResult(prev => ({ ...prev, holdings: prev.holdings.filter((_, i) => i !== idx) }));

  const updateHolding = (idx, field, value) => {
    setResult(prev => ({
      ...prev,
      holdings: prev.holdings.map((h, i) => i === idx ? { ...h, [field]: value } : h)
    }));
  };

  const brokerLabel = getBrokerLabel(platform, customPlatform, accountType, customAccountType);
  const platformValid = platform && (platform !== 'Other' || customPlatform.trim());
  const accountValid = accountType && (accountType !== 'Other' || customAccountType.trim());

  const handleConfirm = async () => {
    if (!result?.holdings?.length || !platformValid || !accountValid) return;
    setConfirming(true);
    try {
      const res = await fetch('/api/confirm-holdings', { method: 'POST', headers: authHeaders({ 'Content-Type': 'application/json' }), body: JSON.stringify({ holdings: result.holdings, broker: brokerLabel, currency }) });
      const data = await res.json();
      if (data.success) { setSuccess(true); setAddedCount(data.added); }
      else setError(data.error || 'Failed to sync holdings');
    } catch { setError('Failed to sync holdings'); }
    setConfirming(false);
  };

  const reset = () => { setImageFile(null); setImagePreview(null); setResult(null); setError(null); setPlatform(''); setCustomPlatform(''); setAccountType(''); setCustomAccountType(''); setCurrency('USD'); setSuccess(false); };

  if (success) {
    return (
      <div className="glass-card" style={{ textAlign: 'center', padding: '50px 30px' }}>
        <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#00ff9d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 12 }}>
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
        </svg>
        <h2 style={{ color: '#00ff9d', marginBottom: 8 }}>Holdings Synced</h2>
        <p style={{ color: '#94a3b8', marginBottom: 8 }}>{addedCount} holdings synced to <strong style={{ color: '#e2e8f0' }}>{brokerLabel}</strong> ({currency})</p>
        <p style={{ color: '#64748b', marginBottom: 24 }}>Your portfolio has been updated</p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button className="btn-primary" onClick={reset}>Sync Another</button>
          <button className="btn-secondary" onClick={() => navigate('/portfolio')}>View Portfolio</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {error && <div className="alert-box alert-error">{error}</div>}
      {!imagePreview && <UploadZone dragOver={dragOver} setDragOver={setDragOver} handleDrop={handleDrop} handlePasteClick={handlePasteClick} fileRef={fileRef} cameraRef={cameraRef} handleFile={handleFile} label="portfolio holdings page" />}

      {imagePreview && !result && (
        <div className="glass-card" style={{ textAlign: 'center', padding: '30px' }}>
          <img src={imagePreview} alt="Holdings screenshot" style={{ maxWidth: '100%', maxHeight: 400, borderRadius: 12, marginBottom: 20, border: '1px solid rgba(0,255,157,0.2)' }} />
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="btn-primary" onClick={handleAnalyze} disabled={analyzing} style={analyzing ? { opacity: 0.7 } : { boxShadow: '0 0 20px rgba(0,255,157,0.3)' }}>
              {analyzing ? <><span className="spinner" style={{ marginRight: 8 }} />Analyzing...</> : 'Analyze with AI Vision'}
            </button>
            <button className="btn-secondary" onClick={reset}>Choose Different Image</button>
          </div>
        </div>
      )}

      {result && result.holdings && (
        <div className="glass-card" style={{ padding: '30px' }}>
          <h3 style={{ color: '#00ff9d', marginBottom: 6 }}>{result.holdings.length} Holdings Detected</h3>
          <p style={{ color: '#64748b', fontSize: 12, marginBottom: 20 }}>Click any cell to edit if the AI returned incorrect values</p>
          <div style={{ overflowX: 'auto', marginBottom: 24 }}>
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 4px' }}>
              <thead>
                <tr>
                  {['Ticker', 'Shares', 'Avg Price', 'Value', ''].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: '#64748b', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 500 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.holdings.map((h, i) => (
                  <tr key={i} style={{ background: 'rgba(0,0,0,0.2)' }}>
                    <td style={{ padding: '6px 8px', borderRadius: '8px 0 0 8px' }}>
                      <input value={h.ticker || ''} onChange={e => updateHolding(i, 'ticker', e.target.value.toUpperCase())} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, color: '#e2e8f0', fontWeight: 600, fontSize: 14, padding: '6px 8px', width: '100%', minWidth: 70, fontFamily: 'Inter, sans-serif' }} />
                    </td>
                    <td style={{ padding: '6px 8px' }}>
                      <input type="number" value={h.shares ?? ''} onChange={e => updateHolding(i, 'shares', parseFloat(e.target.value) || 0)} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, color: '#94a3b8', fontSize: 14, padding: '6px 8px', width: '100%', minWidth: 70, fontFamily: 'Inter, sans-serif' }} />
                    </td>
                    <td style={{ padding: '6px 8px' }}>
                      <input type="number" step="0.01" value={h.avg_price ?? ''} onChange={e => updateHolding(i, 'avg_price', parseFloat(e.target.value) || 0)} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, color: '#94a3b8', fontSize: 14, padding: '6px 8px', width: '100%', minWidth: 90, fontFamily: 'Inter, sans-serif' }} />
                    </td>
                    <td style={{ padding: '6px 8px' }}>
                      <input type="number" step="0.01" value={h.current_value ?? ''} onChange={e => updateHolding(i, 'current_value', parseFloat(e.target.value) || 0)} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, color: '#94a3b8', fontSize: 14, padding: '6px 8px', width: '100%', minWidth: 90, fontFamily: 'Inter, sans-serif' }} />
                    </td>
                    <td style={{ padding: '6px 8px', borderRadius: '0 8px 8px 0', textAlign: 'right' }}>
                      <button onClick={() => removeHolding(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ff4d4d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PlatformSelect platform={platform} setPlatform={setPlatform} customPlatform={customPlatform} setCustomPlatform={setCustomPlatform} accountType={accountType} setAccountType={setAccountType} customAccountType={customAccountType} setCustomAccountType={setCustomAccountType} currency={currency} setCurrency={setCurrency} />
          <div className="alert-box alert-warning" style={{ marginBottom: 20 }}>
            This will replace all existing holdings for the selected platform and account. Holdings from other platforms/accounts won't be affected.
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button className="btn-primary btn-full" onClick={handleConfirm} disabled={confirming || !platformValid || !accountValid || !result.holdings.length} style={!confirming && platformValid && accountValid ? { boxShadow: '0 0 20px rgba(0,255,157,0.3)' } : { opacity: 0.5 }}>
              {confirming ? 'Syncing...' : `Confirm & Sync ${result.holdings.length} Holdings`}
            </button>
            <button className="btn-secondary" onClick={reset} style={{ flex: 'none' }}>Start Over</button>
          </div>
        </div>
      )}
    </div>
  );
}

function WizardStepper({ current }) {
  const labels = ['Instructions', 'Upload File', 'Preview & Map', 'Confirm'];
  return (
    <div>
      <div className="wizard-stepper">
        {labels.map((label, i) => {
          const step = i + 1;
          const cls = step === current ? 'active' : step < current ? 'done' : '';
          return (
            <span key={i} style={{ display: 'contents' }}>
              <div className={`wizard-dot ${cls}`}>{step}</div>
              {step < labels.length && <div className={`wizard-line ${step < current ? 'done' : ''}`} />}
            </span>
          );
        })}
      </div>
      <div className="step-labels">
        {labels.map((label, i) => (
          <span key={i} style={{ color: i + 1 <= current ? '#00ff9d' : '#475569' }}>{label}</span>
        ))}
      </div>
    </div>
  );
}

function CSVImport({ navigate }) {
  const [platforms, setPlatforms] = useState({});
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [step, setStep] = useState(1);
  const [csvData, setCsvData] = useState(null);
  const [mapping, setMapping] = useState({ ticker: null, shares: null, buy_price: null });
  const [importResult, setImportResult] = useState(null);
  const [platform, setPlatform] = useState('');
  const [customPlatform, setCustomPlatform] = useState('');
  const [accountType, setAccountType] = useState('');
  const [customAccountType, setCustomAccountType] = useState('');
  const [currency, setCurrency] = useState('USD');

  const brokerLabel = getBrokerLabel(platform, customPlatform, accountType, customAccountType);
  const platformValid = platform && (platform !== 'Other' || customPlatform.trim());
  const accountValid = accountType && (accountType !== 'Other' || customAccountType.trim());

  useEffect(() => { fetchPlatforms().then(setPlatforms); }, []);

  function selectPlatform(key) {
    setSelectedPlatform(key);
    setStep(1);
    setCsvData(null);
    setMapping({ ticker: null, shares: null, buy_price: null });
    setImportResult(null);
    const plat = platforms[key];
    if (plat) {
      const match = PLATFORMS.find(p => p.toLowerCase() === plat.name.toLowerCase());
      if (match) setPlatform(match);
      else { setPlatform('Other'); setCustomPlatform(plat.name); }
    }
  }

  function backToBrokers() {
    setSelectedPlatform(null);
    setStep(1);
    setCsvData(null);
    setPlatform('');
    setCustomPlatform('');
    setAccountType('');
    setCustomAccountType('');
    setCurrency('USD');
  }

  async function handleFileUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    const result = await parseCSV(file);
    if (result.error) { alert(result.error); return; }
    setCsvData(result);
  }

  async function handleNextFromUpload() {
    if (!csvData) return;
    const detected = await autoDetectColumns(csvData.columns, selectedPlatform);
    setMapping(detected);
    setStep(3);
  }

  async function handleImport() {
    if (!csvData || !mapping.ticker || !mapping.shares || !mapping.buy_price || !platformValid || !accountValid) return;
    const holdings = csvData.preview
      .map(row => ({
        ticker: String(row[mapping.ticker] || '').trim(),
        shares: parseFloat(row[mapping.shares]) || 0,
        buy_price: parseFloat(row[mapping.buy_price]) || 0,
      }))
      .filter(h => h.ticker && h.shares > 0 && h.buy_price > 0);
    const result = await importHoldings(holdings, brokerLabel, currency);
    setImportResult(result);
    setStep(4);
  }

  if (!selectedPlatform) {
    return (
      <div>
        <div className="platform-grid">
          {Object.entries(platforms).map(([key, plat]) => (
            <div className="platform-card" key={key} onClick={() => selectPlatform(key)}>
              <div className="p-flag">{plat.flag}</div>
              <div className="p-name">{plat.name}</div>
              <div className="p-desc">{plat.desc}</div>
            </div>
          ))}
        </div>
        <p style={{ textAlign: 'center', color: '#475569', marginTop: 8, fontSize: '0.85rem' }}>
          Select your broker above to start the CSV import wizard
        </p>
      </div>
    );
  }

  const plat = platforms[selectedPlatform];

  return (
    <div>
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <h2 style={{ color: '#f1f5f9', marginBottom: 4 }}>{plat.flag} Import from {plat.name}</h2>
        <span style={{ color: '#475569', fontSize: '0.85rem' }}>Step {step} of 4</span>
      </div>

      <WizardStepper current={step} />

      {step === 1 && (
        <div>
          <div className="glass-card" style={{ marginBottom: 16 }}>
            <h3 style={{ color: '#f1f5f9', marginTop: 0, marginBottom: 12 }}>Select your account</h3>
            <PlatformSelect platform={platform} setPlatform={setPlatform} customPlatform={customPlatform} setCustomPlatform={setCustomPlatform} accountType={accountType} setAccountType={setAccountType} customAccountType={customAccountType} setCustomAccountType={setCustomAccountType} currency={currency} setCurrency={setCurrency} />
          </div>
          <div className="glass-card">
            <h3 style={{ color: '#f1f5f9', marginTop: 0, marginBottom: 12 }}>How to export your data</h3>
            {plat.instructions.map((line, i) => (
              <p key={i} style={{ color: '#94a3b8', margin: '8px 0', lineHeight: 1.6 }}>{line}</p>
            ))}
          </div>
          <div className="currency-badge" style={{ marginTop: 12 }}>
            <span className="label">Expected file: </span>
            <span className="value" style={{ fontFamily: 'monospace' }}>{plat.file_hint}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
            <button className="btn-secondary" onClick={backToBrokers}>Back to Brokers</button>
            <button className="btn-primary" onClick={() => setStep(2)} disabled={!platformValid || !accountValid}>Next</button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div>
          <div className="upload-zone">
            <p style={{ color: '#94a3b8', fontSize: '1.1rem', marginBottom: 4 }}>Upload your CSV file</p>
            <p style={{ color: '#475569', fontSize: '0.85rem', marginBottom: 16 }}>Drag & drop or click to browse</p>
            <input type="file" accept=".csv" onChange={handleFileUpload} style={{ color: '#94a3b8' }} />
          </div>
          {csvData && (
            <div className="alert-box alert-success">
              File loaded — {csvData.total_rows} rows, {csvData.columns.length} columns detected
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
            <button className="btn-secondary" onClick={() => setStep(1)}>Back</button>
            <button className="btn-primary" onClick={handleNextFromUpload} disabled={!csvData}>Next</button>
          </div>
        </div>
      )}

      {step === 3 && csvData && (
        <div>
          <h3 style={{ marginBottom: 12 }}>Preview Data (first {csvData.preview.length} rows)</h3>
          <div className="glass-card" style={{ padding: 0, overflow: 'auto', marginBottom: 16 }}>
            <table className="data-table">
              <thead>
                <tr>{csvData.columns.map(c => <th key={c}>{c}</th>)}</tr>
              </thead>
              <tbody>
                {csvData.preview.slice(0, 10).map((row, i) => (
                  <tr key={i}>{csvData.columns.map(c => <td key={c}>{String(row[c] ?? '')}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
          <hr />
          <h3 style={{ marginBottom: 8 }}>Column Mapping</h3>
          <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: 16 }}>We auto-detected your columns below. Adjust if needed.</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
            {[
              { label: 'Ticker / Symbol', field: 'ticker' },
              { label: 'Shares / Quantity', field: 'shares' },
              { label: 'Avg Buy Price', field: 'buy_price' },
            ].map(({ label, field }) => (
              <div className="form-group" key={field}>
                <label className="form-label">{label}</label>
                <select className="input-field" value={mapping[field] || ''} onChange={e => setMapping(prev => ({ ...prev, [field]: e.target.value || null }))}>
                  <option value="">(not set)</option>
                  {csvData.columns.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            ))}
          </div>
          {(!mapping.ticker || !mapping.shares || !mapping.buy_price) && (
            <div className="alert-box alert-warning">Please map all three columns before proceeding.</div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
            <button className="btn-secondary" onClick={() => setStep(2)}>Back</button>
            <button className="btn-primary" onClick={handleImport} disabled={!mapping.ticker || !mapping.shares || !mapping.buy_price}>Import</button>
          </div>
        </div>
      )}

      {step === 4 && (
        <div>
          <div className="glass-card" style={{ textAlign: 'center', padding: 40 }}>
            <h2 style={{ color: '#00ff9d', marginBottom: 12 }}>Import Complete!</h2>
            {importResult && (
              <p style={{ color: '#94a3b8', fontSize: '1.1rem' }}>
                Successfully added <strong style={{ color: '#00ff9d' }}>{importResult.added}</strong> holdings to <strong style={{ color: '#e2e8f0' }}>{brokerLabel}</strong>.
              </p>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 16 }}>
            <button className="btn-secondary" onClick={backToBrokers}>Import More</button>
            <button className="btn-primary" onClick={() => navigate('/')}>Go to Dashboard</button>
          </div>
        </div>
      )}
    </div>
  );
}

function UploadZone({ dragOver, setDragOver, handleDrop, handlePasteClick, fileRef, cameraRef, handleFile, label }) {
  return (
    <div
      className="glass-card"
      style={{
        border: dragOver ? '2px solid #00ff9d' : '2px dashed rgba(0,255,157,0.3)',
        textAlign: 'center',
        padding: '50px 30px',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        background: dragOver ? 'rgba(0,255,157,0.05)' : undefined,
      }}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      onClick={() => fileRef.current?.click()}
    >
      <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#00ff9d" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 12, opacity: 0.7 }}>
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
      </svg>
      <h3 style={{ color: '#e2e8f0', marginBottom: 6, fontSize: 16 }}>Drop, paste, or upload your {label} screenshot</h3>
      <p style={{ color: '#64748b', marginBottom: 20, fontSize: 13 }}>Drag & drop, press Ctrl+V to paste, or use the buttons below</p>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
        <button className="btn-primary" onClick={(e) => { e.stopPropagation(); handlePasteClick(); }} style={{ fontSize: 13, padding: '8px 16px' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 5, verticalAlign: 'middle' }}>
            <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
            <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
          </svg>
          Paste
        </button>
        <button className="btn-secondary" onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }} style={{ fontSize: 13, padding: '8px 16px' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 5, verticalAlign: 'middle' }}>
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
          Choose File
        </button>
        <button className="btn-secondary" onClick={(e) => { e.stopPropagation(); cameraRef.current?.click(); }} style={{ fontSize: 13, padding: '8px 16px' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 5, verticalAlign: 'middle' }}>
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
            <circle cx="12" cy="13" r="4" />
          </svg>
          Camera
        </button>
      </div>
      <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])} />
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])} />
    </div>
  );
}

function getBrokerLabel(platform, customPlatform, accountType, customAccountType) {
  const plat = platform === 'Other' ? customPlatform.trim() : platform;
  const acct = accountType === 'Other' ? customAccountType.trim() : accountType;
  if (!plat || !acct) return '';
  return `${plat} — ${acct}`;
}

function PlatformSelect({ platform, setPlatform, customPlatform, setCustomPlatform, accountType, setAccountType, customAccountType, setCustomAccountType, currency, setCurrency }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Trading Platform</label>
          <select className="input-field" value={platform} onChange={e => { setPlatform(e.target.value); if (e.target.value !== 'Other') setCustomPlatform(''); }}>
            <option value="">Select your platform...</option>
            {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          {platform === 'Other' && (
            <input className="input-field" placeholder="Enter platform name..." value={customPlatform} onChange={e => setCustomPlatform(e.target.value)} style={{ marginTop: 8 }} />
          )}
        </div>
        <div className="form-group">
          <label className="form-label">Account Type</label>
          <select className="input-field" value={accountType} onChange={e => { setAccountType(e.target.value); if (e.target.value !== 'Other') setCustomAccountType(''); }}>
            <option value="">Select account type...</option>
            {ACCOUNT_TYPES.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          {accountType === 'Other' && (
            <input className="input-field" placeholder="Enter account type..." value={customAccountType} onChange={e => setCustomAccountType(e.target.value)} style={{ marginTop: 8 }} />
          )}
        </div>
      </div>
      <div className="form-row" style={{ marginTop: 8 }}>
        <div className="form-group">
          <label className="form-label">Currency</label>
          <select className="input-field" value={currency} onChange={e => setCurrency(e.target.value)}>
            {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="form-group" />
      </div>
    </div>
  );
}

function EditableField({ label, value, onChange, type = 'text', prefix = '' }) {
  const [editing, setEditing] = useState(false);
  const [tempVal, setTempVal] = useState(value);

  const startEdit = () => { setTempVal(value); setEditing(true); };
  const finishEdit = () => { onChange(tempVal); setEditing(false); };

  const displayVal = type === 'number' && value && prefix
    ? `${prefix}${parseFloat(value).toFixed(2)}`
    : (value || '\u2014');

  return (
    <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: 10, padding: '12px 16px' }}>
      <div style={{ color: '#64748b', fontSize: 11, marginBottom: 3, textTransform: 'uppercase', letterSpacing: 1 }}>{label}</div>
      {editing ? (
        <input
          autoFocus
          type={type}
          step={type === 'number' ? '0.01' : undefined}
          value={tempVal}
          onChange={e => setTempVal(e.target.value)}
          onBlur={finishEdit}
          onKeyDown={e => { if (e.key === 'Enter') finishEdit(); }}
          style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(0,255,157,0.3)', borderRadius: 6, color: '#e2e8f0', fontSize: 17, fontWeight: 600, padding: '4px 8px', width: '100%', fontFamily: 'Inter, sans-serif', outline: 'none' }}
        />
      ) : (
        <div
          onClick={startEdit}
          style={{ color: '#e2e8f0', fontSize: 17, fontWeight: 600, cursor: 'pointer', padding: '4px 0', borderBottom: '1px dashed rgba(255,255,255,0.15)' }}
          title="Click to edit"
        >{displayVal}</div>
      )}
    </div>
  );
}

export default ImportPortfolio;
